import React, { useEffect, useState } from 'react';
import { Title } from '@gerdau/hefesto';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import GoBackButtonFacelift from '../../../../../../components/GoBackButton/Facelift';
import {
  IPavement,
  IPavementOrder
} from '../../../../../../store/ducks/fabrication-mockups/types';
import { Container } from './styles';
import { EFabricationOrderStatus } from '../../../../../../store/ducks/fabrication-orders/types';
import moment from 'moment';
import FabricationOrderStatusTag from '../../../../../../components/FabricationOrderStatusTag';
import DownloadOrderColumn from './DownloadOrderColumn';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../../utils/Tagging';
import Table from '../../../../../../components/Table';

interface IPavementOrdersTableDataSource {
  key: number;
  order: IPavementOrder;
  totalWeight: number;
  sendDate: string;
  orderStatus: EFabricationOrderStatus;
}

export interface IPavementOrdersProps {
  pavement: IPavement;
  contractNumber: string;
}

const PavementOrders: React.FC = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { pavement, contractNumber } = location.state as IPavementOrdersProps;

  const { screenName = '' } = getTaggingInformationScreen(
    'mockups_pavement-orders-page'
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        page: window.location.pathname,
        screenName,
        event: 'page_view'
      })
    );
  }, []);

  const title = `${pavement.name} - Pedidos`;

  const [pavementOrdersData, setPavementOrdersData] = useState<
    IPavementOrdersTableDataSource[]
  >([]);

  useEffect(() => {
    const data: IPavementOrdersTableDataSource[] =
      pavement.orders?.map((order, idx) => {
        return {
          key: idx,
          order,
          sendDate: order.sendDate || ' - ',
          totalWeight: order.totalWeight || 0,
          orderStatus: order.orderStatus.toString() as EFabricationOrderStatus
        };
      }) || [];

    setPavementOrdersData(data);
  }, [pavement.orders]);

  const columns = [
    {
      width: '15%',
      title: 'Número do pedido',
      dataIndex: 'order',
      sorter: (
        a: IPavementOrdersTableDataSource,
        b: IPavementOrdersTableDataSource
      ) => a.order.orderNumber.localeCompare(b.order.orderNumber),
      render: (order: IPavementOrder) => <>{order.orderNumber || ' - '}</>
    },
    {
      width: '8%',
      title: 'Data do pedido',
      dataIndex: 'sendDate',
      sorter: (
        a: IPavementOrdersTableDataSource,
        b: IPavementOrdersTableDataSource
      ) =>
        moment(a.sendDate).toDate().getTime() -
        moment(b.sendDate).toDate().getTime(),
      render: (sendDate: string) => (
        <>{moment(sendDate).format('DD/MM/YYYY, HH:mm')}</>
      )
    },
    {
      width: '5%',
      title: 'Peso total',
      dataIndex: 'totalWeight',
      sorter: (
        a: IPavementOrdersTableDataSource,
        b: IPavementOrdersTableDataSource
      ) => a.totalWeight - b.totalWeight,
      render: (totalWeight: number) => <>{totalWeight ? totalWeight : 0}</>
    },
    {
      width: '15%',
      title: 'Status do pedido',
      dataIndex: 'orderStatus',
      sorter: (
        a: IPavementOrdersTableDataSource,
        b: IPavementOrdersTableDataSource
      ) => a.orderStatus.localeCompare(b.orderStatus),
      render: (orderStatus: EFabricationOrderStatus) => (
        <FabricationOrderStatusTag status={orderStatus} />
      )
    },
    {
      width: '5%',
      title: 'Opções',
      dataIndex: 'order',
      render: (order: IPavementOrder) => (
        <div data-testid="download-order">
          <DownloadOrderColumn
            orderId={order.id}
            contractNumber={contractNumber}
          />
        </div>
      )
    }
  ];

  return (
    <Container>
      <header>
        <GoBackButtonFacelift title="Voltar" onClick={() => history.goBack()} />

        <Title level={1}>{title}</Title>
        <span>
          Consulte aqui os dados sobre os pedidos relacionados a este pavimento
        </span>
      </header>

      <div className="table-container">
        <Table columns={columns} data={pavementOrdersData} hasPagination />
      </div>
    </Container>
  );
};

export default PavementOrders;
